import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Cropper from 'react-easy-crop';
import { useAuthSelector } from '../../services/selectors/authSelector';
import { useCustomDispatch } from '../../hooks';
import { setLatestDriverPhotoUrl } from '../../services/slices/setting/driverProfilePhoto';
import { fetchDigitalIdCard, saveDigitalIdCardPhoto } from '../../services/apis/driver/getDigitalIdCard';
import * as routeNames from '../../routes/routeName';
import { SOSemergencyEnableButton } from '../../components/common/SOSemergencyEnableButton';
import { LoadingView } from '../../components/common/loader';
import { getDefaultPharmacist } from '../../utils/helper/helper';
import AnimatedBottomSheet from '../../components/common/animatedBottomSheet';

const ICON_BLUE = '#2563eb';
const ICON_BG = '#e0f2fe';

/** Convert data URL to File for API upload */
const dataURLToFile = (dataUrl, fileName = 'photo.jpg') => {
	const arr = dataUrl.split(',');
	const mimeMatch = arr[0].match(/:(.*?);/);
	const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
	const bstr = atob(arr[1]);
	let n = bstr.length;
	const u8arr = new Uint8Array(n);
	while (n--) u8arr[n] = bstr.charCodeAt(n);
	return new File([u8arr], fileName, { type: mime });
};

/** Create cropped image as data URL (square crop for profile photo) */
const getCroppedImg = (imageSrc, pixelCrop) => {
	return new Promise((resolve, reject) => {
		const image = new Image();
		image.setAttribute('crossOrigin', 'anonymous');
		image.onload = () => {
			const canvas = document.createElement('canvas');
			const size = Math.min(pixelCrop.width, pixelCrop.height);
			canvas.width = size;
			canvas.height = size;
			const ctx = canvas.getContext('2d');
			if (!ctx) {
				reject(new Error('Canvas not supported'));
				return;
			}
			ctx.drawImage(
				image,
				pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
				0, 0, size, size
			);
			canvas.toBlob((blob) => {
				if (!blob) {
					reject(new Error('Canvas toBlob failed'));
					return;
				}
				const reader = new FileReader();
				reader.onloadend = () => resolve(reader.result);
				reader.onerror = reject;
				reader.readAsDataURL(blob);
			}, 'image/jpeg', 0.92);
		};
		image.onerror = () => reject(new Error('Image load failed'));
		image.src = imageSrc;
	});
};

const OutlinedPhoneIcon = () => (
	<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke={ICON_BLUE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
		<path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
	</svg>
);

const OutlinedFaxIcon = () => (
	<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke={ICON_BLUE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
		<path d="M6 18H4a2 2 0 01-2-2v-8a2 2 0 012-2h16a2 2 0 012 2v8a2 2 0 01-2 2h-2" />
		<path d="M6 14h12v4H6z" />
		<path d="M6 6h2v4H6V6z" />
		<path d="M10 6h2v4h-2V6z" />
		<path d="M14 6h2v4h-2V6z" />
	</svg>
);

const OutlinedLocationIcon = () => (
	<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke={ICON_BLUE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
		<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
		<circle cx="12" cy="10" r="3" />
	</svg>
);

const UserIcon = () => (
	<svg width="56" height="56" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
		<circle cx="12" cy="8" r="4" />
		<path d="M4 20c0-4 4-6 8-6s8 2 8 6" />
	</svg>
);

const CredentialRow = ({ icon, value, center }) => {
	const lines = Array.isArray(value) ? value.filter(Boolean) : value != null && value !== '' ? [value] : [];
	if (lines.length === 0) return null;
	return (
		<div className={`flex gap-2.5 items-center ${center ? 'justify-center' : ''}`}>
			<div
				className={`flex-shrink-0 w-8 h-8 rounded-md flex items-center justify-center ${center ? '' : 'mt-0.5'}`}
				style={{ backgroundColor: ICON_BG }}
			>
				{icon}
			</div>
			<div className={`min-w-0 space-y-0 ${center ? 'text-center flex-shrink-0' : 'flex-1'}`}>
				{lines.map((line, i) => (
					<p key={i} className="text-[15px] font-bold text-[#1f2937] leading-tight">{line}</p>
				))}
			</div>
		</div>
	);
};

const DriverDigitalCard = () => {
	const navigate = useNavigate();
	const dispatch = useCustomDispatch();
	const { driverSelectedPharmacy, driverDefaultPharmacist } = useAuthSelector();
	const pharmacyId = driverSelectedPharmacy?.pharmacy_id;
	const defaultPharmacist = useMemo(
		() => getDefaultPharmacist(pharmacyId, driverDefaultPharmacist),
		[driverDefaultPharmacist, pharmacyId]
	);
	const pharmacistUser = defaultPharmacist?.[0]?.driver_pharmacist_user;
	const isDriverForAuthorizationLetter = pharmacistUser?.type !== 'pharmacist';

	const [cardData, setCardData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [profileImageError, setProfileImageError] = useState(false);
	const [localProfileImage, setLocalProfileImage] = useState(null);
	const [showCropModal, setShowCropModal] = useState(false);
	const [imageToCrop, setImageToCrop] = useState(null);
	const [crop, setCrop] = useState({ x: 0, y: 0 });
	const [zoom, setZoom] = useState(1);
	const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
	const [uploadPhotoLoading, setUploadPhotoLoading] = useState(false);
	const [uploadPhotoError, setUploadPhotoError] = useState(null);
	const [showPhotoSourceModal, setShowPhotoSourceModal] = useState(false);
	const fileInputGalleryRef = useRef(null);
	const fileInputCameraRef = useRef(null);

	useEffect(() => {
		if (!pharmacyId) {
			setLoading(false);
			return;
		}
		let cancelled = false;
		setLoading(true);
		setError(null);
		fetchDigitalIdCard(pharmacyId)
			.then((res) => {
				if (!cancelled && res?.success && res?.data) setCardData(res.data);
			})
			.catch((err) => {
				if (!cancelled) setError(err?.response?.data?.message || 'Failed to load digital card');
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => { cancelled = true; };
	}, [pharmacyId]);

	useEffect(() => {
		setProfileImageError(false);
	}, [cardData?.photo]);

	const onCropComplete = useCallback((_croppedArea, croppedAreaPixels) => {
		setCroppedAreaPixels(croppedAreaPixels);
	}, []);

	const handleFileChange = useCallback((e) => {
		const file = e?.target?.files?.[0];
		if (!file || !file.type.startsWith('image/')) return;
		setUploadPhotoError(null);
		const reader = new FileReader();
		reader.onload = () => {
			setImageToCrop(reader.result);
			setCrop({ x: 0, y: 0 });
			setZoom(1);
			setCroppedAreaPixels(null);
			setShowCropModal(true);
		};
		reader.onerror = () => setShowCropModal(false);
		reader.readAsDataURL(file);
		e.target.value = '';
	}, []);

	const handleCropDone = useCallback(async () => {
		if (!imageToCrop || !croppedAreaPixels) {
			setShowCropModal(false);
			return;
		}
		setUploadPhotoError(null);
		let croppedDataUrl = null;
		try {
			croppedDataUrl = await getCroppedImg(imageToCrop, croppedAreaPixels);
			const photoFile = dataURLToFile(croppedDataUrl, `digital-id-photo-${Date.now()}.jpg`);
			setShowCropModal(false);
			setImageToCrop(null);
			setCroppedAreaPixels(null);

			setUploadPhotoLoading(true);
			const result = await saveDigitalIdCardPhoto(photoFile, photoFile.name);
			if (result?.success && result?.data?.photo_url) {
				const photoUrl = result.data.photo_url;
				setCardData((prev) => (prev ? { ...prev, photo: photoUrl } : prev));
				setLocalProfileImage(null);
				setProfileImageError(false);
				dispatch(setLatestDriverPhotoUrl(photoUrl));
			} else {
				setUploadPhotoError(result?.message || 'Failed to save photo');
				setLocalProfileImage(croppedDataUrl);
			}
		} catch (err) {
			const message = err?.response?.data?.message || err?.message || 'Failed to save photo';
			setUploadPhotoError(message);
			if (croppedDataUrl) setLocalProfileImage(croppedDataUrl);
		} finally {
			setUploadPhotoLoading(false);
		}
	}, [imageToCrop, croppedAreaPixels]);

	const handleCropCancel = useCallback(() => {
		setShowCropModal(false);
		setImageToCrop(null);
		setCroppedAreaPixels(null);
	}, []);

	const openPhotoSourceModal = useCallback(() => {
		if (!uploadPhotoLoading) setShowPhotoSourceModal(true);
	}, [uploadPhotoLoading]);

	const openGallery = useCallback(() => {
		setShowPhotoSourceModal(false);
		if (fileInputGalleryRef.current) fileInputGalleryRef.current.click();
	}, []);

	const openCamera = useCallback(() => {
		setShowPhotoSourceModal(false);
		if (fileInputCameraRef.current) fileInputCameraRef.current.click();
	}, []);

	// Pharmacy details from digital card API response
	const pharmacy = cardData?.pharmacy;
	const displayName = cardData?.full_name;
	const dob = cardData?.dob;
	const avatar = cardData?.photo;
	const phone = pharmacy?.phone;
	const fax = pharmacy?.fax;
	const addressLine1 = pharmacy?.address;
	const addressLine2 = [pharmacy?.city, pharmacy?.zip_code, pharmacy?.country].filter(Boolean).join(', ');

	return (
		<>
			<div className="app-wrapper flex justify-center min-h-screen bg-[#f8f9fa]">
				<div className="app-screen no-shades w-full xl:w-3/12 lg:w-4/12 md:w-5/12 sm:w-6/12 overflow-x-hidden">
					<div className="flex items-center justify-center">
						<SOSemergencyEnableButton />
					</div>
					<div className="relative p-4 pb-10">
						{loading && (
							<div className="flex items-center justify-center py-12">
								<LoadingView />
							</div>
						)}
						{error && !loading && (
							<div className="rounded-xl bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm mb-4">
								{error}
							</div>
						)}
						{uploadPhotoError && (
							<div className="rounded-xl bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm mb-4">
								{uploadPhotoError}
							</div>
						)}
						{!loading && cardData && (
						<>
						{/* Top header – company branding */}
						<div
							className="text-center py-8 px-4 rounded-t-2xl"
							style={{
								background: 'linear-gradient(180deg, #e8eaed 0%, #f1f3f4 50%, #f8f9fa 100%)',
							}}
						>
							{pharmacy?.name && (
								<h1 className="text-2xl font-bold text-[#1f2937] tracking-tight">
									{pharmacy.name.toUpperCase()}
								</h1>
							)}
							<p className="text-sm text-[#9ca3af] mt-2 font-medium">Digital ID Card</p>
						</div>

						{/* Main content card – white, rounded, soft shadow */}
						<div
							className="bg-white rounded-2xl overflow-hidden mx-1 -mt-1"
							style={{
								boxShadow: '0 4px 20px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)',
							}}
						>
							{/* Driver profile section */}
							<div className="pt-8 pb-5 px-6 text-center">
								<input
									ref={fileInputGalleryRef}
									type="file"
									accept="image/*"
									onChange={handleFileChange}
									className="hidden"
									aria-hidden="true"
								/>
								<input
									ref={fileInputCameraRef}
									type="file"
									accept="image/*"
									capture="user"
									onChange={handleFileChange}
									className="hidden"
									aria-hidden="true"
								/>
								<div className="flex justify-center mb-4">
									<div className="relative inline-block">
										{uploadPhotoLoading && (
											<div className="absolute inset-0 z-10 rounded-xl bg-black/50 flex items-center justify-center" aria-hidden="true">
												<div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
											</div>
										)}
										<button
											type="button"
											onClick={openPhotoSourceModal}
											disabled={uploadPhotoLoading}
											className="w-28 h-28 rounded-xl bg-gray-100 flex items-center justify-center overflow-hidden focus:outline-none shadow-inner disabled:opacity-80 disabled:pointer-events-none"
											aria-label="Change profile photo"
										>
											{localProfileImage ? (
												<img
													src={localProfileImage}
													alt={displayName ?? 'Profile'}
													className="w-full h-full object-cover"
												/>
											) : avatar && !profileImageError ? (
												<img
													src={avatar}
													alt={displayName ?? ''}
													className="w-full h-full object-cover"
													onError={() => setProfileImageError(true)}
												/>
											) : (
												<UserIcon />
											)}
										</button>
									</div>
								</div>
								{/* Photo source sheet: Gallery or Camera */}
								<AnimatedBottomSheet isOpen={showPhotoSourceModal} onClose={() => setShowPhotoSourceModal(false)} ariaLabel="Update Profile Photo">
											<div className="bg-white px-4 pt-1 pb-6">
												<h3 className="text-center text-base font-bold text-[#1f2937] mb-3">Update profile photo</h3>
												<div className="space-y-2">
													<button
														type="button"
														onClick={openGallery}
														className="w-full flex items-center gap-3 py-3 px-4 rounded-xl border border-gray-100 bg-gray-50/80 hover:bg-[#e0f2fe] hover:border-blue-200 active:scale-[0.99] transition-all text-left group"
													>
														<div className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center border border-gray-100 group-hover:bg-[#e0f2fe] transition-colors" style={{ backgroundColor: ICON_BG }}>
															<svg className="w-5 h-5" style={{ color: ICON_BLUE }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
																<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
															</svg>
														</div>
														<div className="min-w-0 flex-1">
															<p className="font-semibold text-[#1f2937] text-sm">Gallery</p>
															<p className="text-xs text-gray-500">Choose from your photos</p>
														</div>
														<svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
															<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
														</svg>
													</button>
													<button
														type="button"
														onClick={openCamera}
														className="w-full flex items-center gap-3 py-3 px-4 rounded-xl border border-gray-100 bg-gray-50/80 hover:bg-[#e0f2fe] hover:border-blue-200 active:scale-[0.99] transition-all text-left group"
													>
														<div className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center border border-gray-100 group-hover:bg-[#e0f2fe] transition-colors" style={{ backgroundColor: ICON_BG }}>
															<svg className="w-5 h-5" style={{ color: ICON_BLUE }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
																<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
																<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
																<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 13v7a2 2 0 01-2 2H7a2 2 0 01-2-2v-7" />
															</svg>
														</div>
														<div className="min-w-0 flex-1">
															<p className="font-semibold text-[#1f2937] text-sm">Camera</p>
															<p className="text-xs text-gray-500">Take a new photo</p>
														</div>
														<svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
															<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
														</svg>
													</button>
												</div>
												<button
													type="button"
													onClick={() => setShowPhotoSourceModal(false)}
													className="w-full mt-3 py-3 rounded-xl font-semibold text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 transition-colors"
												>
													Cancel
												</button>
											</div>
								</AnimatedBottomSheet>
								{displayName && (
									<h2 className="text-xl font-bold text-[#1f2937]">{displayName}</h2>
								)}
								{dob && (
									<p className="text-sm text-[#6b7280] mt-1">{dob}</p>
								)}
							</div>

							{/* Divider */}
							<div className="border-t border-gray-100" />

							{/* Pharmacy credentials section */}
							<div className="px-6 py-5 flex flex-col items-start justify-center">
								<p className="text-xs uppercase tracking-[0.15em] text-[#9ca3af] font-semibold mb-3 text-left">
									Pharmacy Credentials
								</p>
								<div className="flex flex-col items-start justify-center space-y-3 w-full">
									{phone != null && phone !== '' && <CredentialRow icon={<OutlinedPhoneIcon />} value={phone} center />}
									{fax != null && fax !== '' && <CredentialRow icon={<OutlinedFaxIcon />} value={fax} center />}
									{(addressLine1 || addressLine2) && (
										<CredentialRow
											icon={<OutlinedLocationIcon />}
											value={[addressLine1, addressLine2].filter(Boolean)}
										/>
									)}
								</div>
							</div>

							{isDriverForAuthorizationLetter ? (
								<>
									<div className="border-t border-gray-100" />
									<div className="px-6 py-5">
										<button
											type="button"
											onClick={() => navigate(routeNames.DRIVER_AUTHORIZATION_LETTER)}
											className="w-full rounded-xl border border-amber-200 bg-amber-50 px-4 py-4 text-left transition-colors hover:bg-amber-100"
										>
											<p className="text-xs font-semibold uppercase tracking-[0.16em] text-amber-700">Driver Document</p>
											<p className="mt-1 text-base font-bold text-[#1f2937]">Authorization Letter</p>
										</button>
									</div>
								</>
							) : null}
						</div>

						{/* Close card button */}
						<button
							type="button"
							onClick={() => navigate(routeNames.DASHBOARD_PHARMACY)}
							className="w-full mt-8 py-4 rounded-xl font-bold text-white text-base tracking-wide transition-colors"
							style={{
								background: '#2563eb',
								boxShadow: '0 4px 14px rgba(37, 99, 235, 0.4)',
							}}
						>
							Close Card
						</button>

						{/* Crop modal */}
						{showCropModal && imageToCrop && (
							<div className="fixed inset-0 z-50 flex flex-col bg-black/80">
								<div className="flex-1 flex flex-col min-h-0">
									<div className="flex-shrink-0 h-[40vh] min-h-[200px] w-full relative">
										<Cropper
											image={imageToCrop}
											crop={crop}
											zoom={zoom}
											aspect={1}
											cropShape="rect"
											showGrid={false}
											onCropChange={setCrop}
											onZoomChange={setZoom}
											onCropComplete={onCropComplete}
										/>
									</div>
									<div className="flex-shrink-0 px-4 py-3 bg-[#1f2937]">
										<label className="block text-xs font-medium text-gray-300 mb-2">Zoom</label>
										<input
											type="range"
											min={1}
											max={3}
											step={0.1}
											value={zoom}
											onChange={(e) => setZoom(Number(e.target.value))}
											className="w-full h-2 rounded-lg appearance-none cursor-pointer bg-gray-600 accent-blue-500"
										/>
									</div>
									<div className="flex-shrink-0 flex gap-3 px-4 py-4 bg-[#1f2937]">
										<button
											type="button"
											onClick={handleCropCancel}
											className="flex-1 py-3 rounded-xl font-semibold text-gray-300 bg-gray-600 hover:bg-gray-500 transition-colors"
										>
											Cancel
										</button>
										<button
											type="button"
											onClick={handleCropDone}
											className="flex-1 py-3 rounded-xl font-semibold text-white transition-colors"
											style={{
												background: '#2563eb',
												boxShadow: '0 2px 8px rgba(37, 99, 235, 0.4)',
											}}
										>
											Use Photo
										</button>
									</div>
								</div>
							</div>
						)}
						</>
						)}
					</div>
				</div>
			</div>
		</>
	);
};

export default DriverDigitalCard;
